import { useEffect, useRef, useState } from 'react';
import axios from '../httpServices/httpService';
import api from '../config.json';

// Module-level cache so every report page shares one fetch instead of
// each page independently calling GET reportmaster.
let cachedPromise = null;

function fetchReportMasterData() {
    if (!cachedPromise) {
        cachedPromise = axios
            .get(api.dataEntryUrl + 'reportmaster')
            .then((res) => (res && res.data) || [])
            .catch((err) => {
                console.error('Failed to load reportmaster:', err);
                cachedPromise = null; // allow retry on next mount if the request failed
                return [];
            });
    }
    return cachedPromise;
}

/**
 * useReportDescription(routeName, displayName)
 *
 * Fetches the ReportMaster collection (once, shared across all report pages)
 * and returns the matching document's Description/MetaTags for the given
 * RouteName. RouteName is admin-entered free text, so matching is done
 * case-insensitively against every candidate string provided, with a
 * DisplayName fallback, to stay robust against casing/wording differences
 * between the route path and the stored value.
 *
 * routeName may be a single string or an array of candidate strings (e.g.
 * the component name, the URL path segment, and the visible title) — the
 * first ReportMaster document matching ANY candidate (by RouteName or
 * DisplayName) is used.
 *
 * Returns { description, metaTags, loading } — description is '' when there
 * is no match or no Description set, so callers can simply check truthiness.
 */
export default function useReportDescription(routeName, displayName) {
    const [state, setState] = useState({ description: '', metaTags: [], loading: true });
    const mountedRef = useRef(true);
    const routeKey = Array.isArray(routeName) ? routeName.join('|') : routeName;

    useEffect(() => {
        mountedRef.current = true;
        fetchReportMasterData().then((list) => {
            if (!mountedRef.current) return;

            const candidates = (Array.isArray(routeName) ? routeName : [routeName])
                .concat([displayName])
                .filter(Boolean)
                .map((value) => value.toLowerCase().trim());

            const match = (list || []).find((m) => {
                const mRoute = (m.RouteName || '').toLowerCase().trim();
                const mDisplay = (m.DisplayName || '').toLowerCase().trim();
                return candidates.some((candidate) => candidate === mRoute || candidate === mDisplay);
            });

            setState({
                description: (match && match.Description) || '',
                metaTags: (match && match.MetaTags) || [],
                loading: false,
            });
        });

        return () => {
            mountedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [routeKey, displayName]);

    return state;
}
